import type { LearningContentLanguage } from "../learning/types";

/** SC-16 — Learn tab strings only; full portal i18n deferred to SC-17. */
export type LearnLocale = LearningContentLanguage;

/** Must match the backend `CONSISTENCY_WINDOW_DAYS` that `consistency_pct` is computed over. */
const CONSISTENCY_WINDOW_DAYS = 30;

function activeDays(consistencyPct: number): number {
  return Math.round((consistencyPct / 100) * CONSISTENCY_WINDOW_DAYS);
}

/** Zero-valued evidence is omitted: a certificate should not advertise "0 reviews". */
function enCertEvidence(streak: number, consistency: number, reviews: number): string {
  const parts: string[] = [];
  const days = activeDays(consistency);
  if (streak > 0) parts.push(`${streak}-day streak`);
  if (days > 0) parts.push(`active on ${days} of the last ${CONSISTENCY_WINDOW_DAYS} days`);
  if (reviews > 0) parts.push(`${reviews} review${reviews === 1 ? "" : "s"}`);
  const sentence = parts.join(" · ");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

function hiCertEvidence(streak: number, consistency: number, reviews: number): string {
  const parts: string[] = [];
  const days = activeDays(consistency);
  if (streak > 0) parts.push(`${streak} दिन की लकीर`);
  if (days > 0) parts.push(`पिछले ${CONSISTENCY_WINDOW_DAYS} में से ${days} दिन सक्रिय`);
  if (reviews > 0) parts.push(`${reviews} रिव्यू`);
  return parts.join(" · ");
}

const EN = {
  learnPageTitle: "Learn",
  learnPageSubtitle: "Your plans, practice and progress all in one place.",
  topicsDuePromo: (n: number) => `${n} topic${n === 1 ? "" : "s"} due today`,
  topicsDuePromoHint: (reviewed: number, reads: number) => {
    const parts = ["Weakest first, spaced so they stick."];
    if (reviewed > 0) parts.push(`${reviewed} done so far today.`);
    if (reads > 0) parts.push(`${reads} reading log${reads === 1 ? "" : "s"} today.`);
    return parts.join(" ");
  },
  findYourGaps: "Let's find your gaps",
  findYourGapsHint: (planTitle: string) =>
    `A short quiz on ${planTitle} tells Ziggy which topics to bring back, and how often.`,
  nothingDue: "Nothing due right now",
  nothingDueHint: (reviewed: number, reads: number) => {
    if (reviewed > 0) {
      return `${reviewed} reviewed today. Ziggy will bring these back when they're due.`;
    }
    if (reads > 0) {
      return `${reads} logged today. Ziggy will quiz you on them when they're due.`;
    }
    return "Ziggy will bring your topics back when they're due.";
  },
  startReviewing: "Start reviewing",
  buildingQuizButton: "Building…",
  takeTheQuiz: "Take the quiz",
  newQuiz: "New quiz",
  motivationBanner: (firstName: string) =>
    `Small consistent steps every day lead to big results. Keep going, ${firstName}! 💜`,
  activePlansTitle: (n: number) => `${n} active plan${n === 1 ? "" : "s"}`,
  yourPlans: "Your plans",
  courseTitle: "Your course",
  roadmapNotReadyTitle: "Roadmap not ready yet",
  roadmapNotReadyBody:
    "You asked for a day-by-day plan — generate it now and your modules plus book picks will appear here.",
  generateRoadmap: "Generate my roadmap",
  studyCoachBusy: "Our study coach is busy — try again in a moment.",
  recommendedBooks: "Recommended books",
  done: "Done",
  back: "Back",
  diagnosticScoringFailed: "Something went wrong scoring your answers. Please try again.",
  diagnosticStepPassedUnlock: "You passed — the next roadmap step is unlocked.",
  diagnosticStepPassedOnly: "You passed this step.",
  diagnosticStepFailedNeed70: (pct: number) =>
    `You need at least 70% to unlock the next step. You scored ${pct}%.`,
  diagnosticTopicsQueued: (n: number) =>
    `${n} topic${n === 1 ? "" : "s"} queued for review, weakest first.`,
  diagnosticNothingToQueue: "Nothing new to queue — you're on top of these topics.",
  diagnosticStepQualified: "Step qualified!",
  diagnosticNotQuiteYet: "Not quite yet",
  diagnosticScoreSummary: (correct: number, asked: number) => `${correct} of ${asked} right`,
  diagnosticReviewStepRetry: "Review this step and try the qualify quiz again.",
  diagnosticWhereToFocus: "Where to focus",
  diagnosticYourAnswers: "Your answers",
  diagnosticBackToRoadmap: "Back to roadmap",
  diagnosticTryAgainLater: "Try again later",
  percentComplete: (pct: number) => `${pct}% complete`,
  dayPlan: (days: number) => `~${days} day plan`,
  backToLearn: "Back to Learn",
  yourRoadmap: "Your roadmap",
  readForPleasureTitle: "Just log what you read",
  readForPleasureBody:
    "Read-for-pleasure plans don't use a roadmap — use Log reading on the Learn tab.",
  roadmapLoadFailed: "Could not load your roadmap.",
  generateRoadmapTitle: "Generate your roadmap",
  generateRoadmapBody: "Your plan is saved — generate the day-by-day modules and book picks now.",
  roadmapBuildingTitle: "Building your roadmap…",
  roadmapBuildingBody:
    "Your plan is saved. We're drafting your day-by-day modules and book picks — this takes up to a minute. Feel free to keep browsing; it'll appear here when it's ready.",
  noRoadmapModules: "No roadmap modules yet for this plan.",
  journeyProgress: "Journey progress",
  courseRoadmapLoadFailed: "Could not load your course roadmap.",
  gapRight: (correct: number, asked: number) => `${correct}/${asked} right`,
  diagnosticYouPicked: "You picked",
  diagnosticSkipped: "Skipped —",
  diagnosticAnswerIs: "the answer is",
  loadingAria: "Loading",
  trackRecordActivityDetail: "Activity detail",
  trackRecordSessionBreakdown: "Session breakdown",
  trackRecordLoadFailed: "Could not load this session.",
  trackRecordNoDetail: "No extra detail for this entry.",
  trackRecordScoreLabel: (pct: number) => `Score: ${pct}%`,
  trackRecordEffectiveRecall: (tier: number, selfRated?: number | null) =>
    selfRated != null
      ? `Effective recall tier: ${tier} (you rated ${selfRated})`
      : `Effective recall tier: ${tier}`,
  trackRecordYourPick: " · your pick",
  trackRecordCorrect: " · correct",
  certAttestation: (scorePct: number, streak: number, consistency: number, reviews: number) =>
    [`${scorePct}%`, enCertEvidence(streak, consistency, reviews)].filter(Boolean).join(" · "),
  certEvidence: (streak: number, consistency: number, reviews: number) =>
    enCertEvidence(streak, consistency, reviews),
  loggingForPlan: "Logging for plan",
  roadmapStep: "Roadmap step",
  pickRoadmapStep: "Pick a roadmap step (recommended)",
  newPlan: "New plan",
  trackRecord: "Track record",
  viewAll: "View all",
  showLess: "Show less",
  trackRecordEmpty: "Your reading logs, quizzes, and reviews will show up here as you study.",
  activePlan: "Active plan",
  choosePlan: "Which plan are you studying today?",
  questions: "Questions",
  startDiagnostic: "Start diagnostic",
  startPractice: "Start practice test",
  startAssessment: "Start assessment",
  postReviewQuestions: "Follow-up questions",
  buildingQuiz: "Building quiz…",
  followUpComplete: "Follow-up complete",
  continue: "Continue",
  yourScore: "Your score",
  reconciliationNote: "Schedule note",
  skillAssessment: "Skill assessment",
  skillHint: "Prove mastery of one skill. Score 80% or higher to earn a verifiable certificate.",
  subject: "Subject",
  skillTopic: "Skill to assess",
  diagnosticTitle: "Diagnostic quiz",
  diagnosticHint: "Find your weak topics for this plan.",
  diagnosticNoQuestions: "This diagnostic has no questions",
  diagnosticBuildFailed: "Ziggy couldn't build a quiz from this plan. Try again in a moment.",
  scoring: "Scoring…",
  finishLabel: "Finish",
  skip: "Skip",
  practiceTitle: "Practice test",
  postReviewHint: "Quick check to lock in your recall rating.",
  startFollowUp: "Start follow-up",
  // Quiz runners (practice / skill / post-review)
  runnerPrevious: "Previous",
  runnerNext: "Next",
  runnerSubmitting: "Submitting…",
  runnerSubmitFailed: "Could not submit your answers.",
  runnerQuestionOf: (index: number, total: number) => `Question ${index} of ${total}`,
  runnerSubmitAssessment: "Submit assessment",
  runnerSubmitFollowUp: "Submit follow-up",
  runnerFinishTest: "Finish test",
  runnerNoQuestionsAssessment: "No questions in this assessment",
  runnerNoQuestionsTest: "No questions in this test",
  runnerScoreBusy: "Ziggy couldn't score your test right now. Try again in a minute.",
  runnerTestSubmitFailed: "Something went wrong submitting your test. Please try again.",
  runnerCorrectOfAsked: (correct: number, asked: number) => `${correct} of ${asked} right`,
  runnerScoreFailed: "Something went wrong scoring your answers. Please try again.",
  skillMastered: "Skill mastered",
  skillKeepPractising: "Keep practising",
  skillPassedScore: (pct: number, topic: string) => `You scored ${pct}% on ${topic}.`,
  skillFailedScore: (pct: number) => `You scored ${pct}%. Aim for 80% to earn a certificate.`,
  certificateIssued: "Certificate issued",
  verifyCodeLabel: "Verify code:",
  // PathwayProgressCard
  setupPending: "Setup pending",
  pathwayActive: "Active",
  progress: "Progress",
  finishSetup: "Finish setup",
  pause: "Pause",
  resume: "Resume",
  archive: "Archive",
  restore: "Restore",
  delete: "Delete",
  confirmDelete: "Confirm delete",
  planActionOn: (action: string, planTitle: string) => `${action} — ${planTitle}`,
  statusPaused: "Paused",
  statusCompleted: "Completed",
  statusArchived: "Archived",
  // SkillAssessmentPanel
  skillSubjectTopicRequired: "Enter a subject and skill topic.",
  skillAssessmentStartFailed: "Could not start the assessment.",
  skillSubjectPlaceholder: "Physics",
  skillTopicPlaceholder: "Optics",
  yourCertificates: "Your certificates",
  copyVerifyCode: "Copy verify code",
  verifyCodeCopied: "Verify code copied",
  verifyCodeCopyFailed: "Could not copy the verify code.",
  viewCertificate: "View certificate",
  viewCertificateAria: (subject: string, topic: string) =>
    `View certificate — ${subject} · ${topic}`,
  certificateOfMastery: "Certificate of skill mastery",
  verifiedCredential: "Verified credential",
  issuedOn: "Issued",
  printCertificate: "Print or save",
  thisCredentialIsPublic: "Anyone with this link can confirm the credential — no login required.",
  certificateInvalid:
    "Could not verify this certificate. Check the code or ask the learner for a new link.",
  certificateVerifyFailed: "Couldn't check this certificate right now. Try again in a moment.",
  certificatesLoadFailed: "Couldn't load your certificates. Any you've earned are still safe.",
  // QuizPromoCard — read-for-pleasure variant
  pleasurePromoTitle: "Log your reading",
  pleasurePromoHint: (pathwayTitle: string) =>
    `Track what you read for ${pathwayTitle}. No quizzes — just your reading journal.`,
  // ReadLogSheet
  readLogTitle: "What did you read today?",
  readLogTopicLabel: "Topic",
  readLogDescription: (pathwayTitle: string) =>
    `Log what you studied from ${pathwayTitle}. Ziggy schedules a recall check for tomorrow.`,
  readLogMinutesOptional: "Minutes (optional)",
  readLogNoteOptional: "Note (optional)",
  readLogMinutesPlaceholder: "30",
  readLogTopicPlaceholder: "Thermodynamics — heat engines",
  readLogNotePlaceholder: "Chapter 12, pages 40–58",
  readLogSubjectTopicRequired: "Subject and topic are required.",
  readLogMinutesInvalid: "Minutes must be a positive number.",
  readLogSaveFailed: "Couldn't save your reading log. Please try again.",
  readLogSaved: "Logged — recall scheduled for tomorrow.",
  readLogSaving: "Saving…",
  saveReadingLog: "Save reading log",
  // ProgressReportSheet
  progressReportTitle: "Progress report",
  progressReportDescription: (pathwayTitle: string) =>
    `AI summary of your last 7 days on ${pathwayTitle}.`,
  progressReportNewUpdates: (n: number) =>
    `${n} new update${n === 1 ? "" : "s"} — generate or open your latest report below.`,
  loadingReports: "Loading reports",
  focusNext: "Focus next",
  progressReportEmpty:
    "Generate your first report to see how your reading, reviews, and tests are adding up.",
  finishWithAnswered: (answered: number, total: number) => `Finish (${answered}/${total} answered)`,
  quizProgressAria: (current: number, total: number) => `Question ${current} of ${total}`,
  questionRange: (min: number, max: number) => `${min}–${max} questions`,
  topicsDueOnPlan: (count: number, planTitle: string) =>
    `${count} topic${count === 1 ? "" : "s"} due on ${planTitle}`,
  switchPlanAria: (planTitle: string) => `Change plan — studying ${planTitle}`,
  reviewProgressAria: (done: number, total: number) => `Card ${done} of ${total}`,
  writingReport: "Writing report…",
  generateFreshReport: "Generate fresh report",
  generateProgressReport: "Generate progress report",
  reportGeneratedOn: (date: string, days: number) => `Covers the last ${days} days · ${date}`,
  reportReadyAnnounce: "Fresh report ready.",
  reportError429: "That's enough reports for today — come back tomorrow.",
  reportError503: "Ziggy couldn't write your report right now. Try again shortly.",
  reportErrorGeneric: "Something went wrong generating your report. Please try again.",
  diagnosticError429: "That's enough quizzes for today — come back tomorrow.",
  diagnosticError503: "Ziggy couldn't build a quiz right now. Try again in a minute.",
  diagnosticErrorGeneric: "Something went wrong starting your quiz. Please try again.",
  practiceError429: "That's enough practice tests for today — come back tomorrow.",
  practiceError503: "Ziggy couldn't build your test right now. Try again in a minute.",
  practiceErrorGeneric: "Something went wrong starting your practice test. Please try again.",
  // Empty / error states
  loading: "Loading…",
  plansLoadFailedTitle: "Couldn't load your plans",
  plansLoadFailedBody: "Something went wrong reaching your study plans. Please try again.",
  moduleDisabledTitle: "Learning app not enabled",
  moduleDisabledBody:
    "Your study centre hasn't turned on the learning app yet. Ask your centre admin — there's nothing to retry from here.",
  retry: "Retry",
  buildStudyPlanTitle: "Let's build your study plan",
  buildStudyPlanHint:
    "Tell Ziggy what you're working towards and we'll shape a plan around your goal, your books and your pace.",
  startMyPlan: "Start my plan",
  // PracticeTestSheet extras
  practiceTopicsLabel: "Topics",
  addSubtopicPlaceholder: "Add a subtopic",
  add: "Add",
  practicePickTopicsHint: (pathwayTitle: string) =>
    `Pick topics for ${pathwayTitle}. Ziggy writes the questions and tells you where to focus after.`,
  // ReviewSession
  finishLater: "Finish later",
  recallPrompt:
    "Close your book. Say or write everything you can recall about this — then rate how it went.",
  fromPathway: (pathwayTitle: string) => `From ${pathwayTitle}`,
  iveTriedRateRecall: "I've tried — rate my recall",
  buildingFollowUpQuiz: "Building follow-up quiz…",
  saving: "Saving…",
  postQuizStartFailed: "Couldn't start the follow-up quiz. Please try again.",
  postQuizUnavailable: "Ziggy couldn't build this required follow-up quiz right now. Please retry.",
  reviewSaveFailed: "Couldn't save that review. Please try again.",
  queueCleared: "Queue cleared",
  queueClearedHint: (reviewed: number) =>
    `${reviewed} topic${reviewed === 1 ? "" : "s"} reviewed. Each one comes back a little later than the last — that spacing is what makes it stick.`,
  backToPlans: "Back to my plans",
  gradeBlanked: "Blanked",
  gradeBlankedHint: "Nothing came back",
  gradeShaky: "Shaky",
  gradeShakyHint: "Got there slowly",
  gradeSolid: "Solid",
  gradeSolidHint: "Recalled it",
  gradeInstant: "Instant",
  gradeInstantHint: "Knew it cold",
  // 3D roadmap
  roadmapScrollAria: (title: string) => `${title} — scroll to travel the road`,
  roadmapRoleDescription: "3D roadmap",
  roadmapKeyboardHint: "Use arrow keys to move between checkpoints",
  roadmapNoSteps: "No roadmap steps yet.",
  roadmapListFallback: "Showing the list view — 3D needs graphics support this browser lacks.",
  roadmapCurrentStep: "Current step",
  roadmapCheckpointAnnounce: (title: string) => `Checkpoint: ${title}`,
  checkpointNumbered: (n: number) => `Checkpoint ${n}`,
  phaseLabel: "Phase",
  readsLogged: (n: number) => `${n} read${n === 1 ? "" : "s"}`,
  noReadsYet: "No reads yet",
  qualifyWithQuestions: (n: number) => `Qualify · ${n}+ Qs`,
  coachGuideLabel: "Ziggy · road guide",
  coachScrollForward: "Scroll to drive forward — your journey unfolds ahead.",
  coachEnteringPhase: (title: string) =>
    `Entering ${title}. Clear each checkpoint to advance this phase.`,
  coachCheckpointCleared: "Checkpoint cleared! Keep rolling toward the finish.",
  coachQualifyStep: "You're here — log a read, then qualify this step.",
  coachStepOpen: "This step is open. Read, practice, move on.",
  coachStepLocked: "Finish the previous checkpoint to unlock this one.",
  stepCompleteTitle: "Step complete!",
  stepCompleteBody: "Next module unlocked — keep the streak going",
  recommendedReads: "Recommended reads",
  recommendedReadsHint: "Pick these up as you progress — swipe to browse the shelf.",
  nextUp: (topic: string) => `Next up · ${topic}`,
  yourProgress: "Your progress",
  tapToOpenPlan: "Tap to open your study plan",
  continueLabel: "Continue",
  planProgressAria: (title: string, pct: number) => `${title} — ${pct}% complete`,
  readsLoggedCount: (n: number) => `${n} read${n === 1 ? "" : "s"} logged`,
  noReadsLoggedYet: "No reads logged yet",
  qualifyWithQuestionsLong: (n: number) => `Qualify (${n}+ Qs)`,
  estimatedMinutes: (n: number) => `~${n} min`,
  subjectProgressAria: (subject: string, done: number, total: number) =>
    `${subject} — ${done} of ${total} steps done`,
  yourCentreFallback: "your centre",
  learnerFallbackName: "there",
} as const;

const HI: Record<keyof typeof EN, string | ((...args: never[]) => string)> = {
  learnPageTitle: "सीखें",
  learnPageSubtitle: "आपकी योजनाएँ, अभ्यास और प्रगति — एक ही जगह।",
  topicsDuePromo: (n: number) => `आज ${n} विषय दोहराने हैं`,
  topicsDuePromoHint: (reviewed: number, reads: number) => {
    const parts = ["सबसे कमज़ोर पहले, दोहराव से याद रहेगा।"];
    if (reviewed > 0) parts.push(`आज अब तक ${reviewed} पूरे।`);
    if (reads > 0) parts.push(`आज ${reads} पढ़ाई लॉग।`);
    return parts.join(" ");
  },
  findYourGaps: "अपने कमज़ोर विषय पहचानें",
  findYourGapsHint: (planTitle: string) =>
    `${planTitle} पर छोटी क्विज़ से Ziggy जानता है किन विषयों को कब वापस लाना है।`,
  nothingDue: "अभी कुछ बाकी नहीं",
  nothingDueHint: (reviewed: number, reads: number) => {
    if (reviewed > 0) {
      return `आज ${reviewed} रिव्यू। समय आते ही Ziggy विषय वापस लाएगा।`;
    }
    if (reads > 0) {
      return `आज ${reads} लॉग। समय आते ही Ziggy क्विज़ कराएगा।`;
    }
    return "समय आते ही Ziggy आपके विषय वापस लाएगा।";
  },
  startReviewing: "रिव्यू शुरू करें",
  buildingQuizButton: "बन रही है…",
  takeTheQuiz: "क्विज़ लें",
  newQuiz: "नई क्विज़",
  motivationBanner: (firstName: string) =>
    `रोज़ छोटे कदम बड़े नतीजे लाते हैं। ${firstName}, जारी रखें! 💜`,
  activePlansTitle: (n: number) => `${n} सक्रिय योजना${n === 1 ? "" : "एँ"}`,
  yourPlans: "आपकी योजनाएँ",
  courseTitle: "आपका कोर्स",
  roadmapNotReadyTitle: "रोडमैप अभी तैयार नहीं",
  roadmapNotReadyBody:
    "आपने दिन-ब-दिन योजना मांगी — अभी जनरेट करें और आपके मॉड्यूल व पुस्तक सुझाव यहाँ दिखेंगे।",
  generateRoadmap: "मेरा रोडमैप बनाएं",
  studyCoachBusy: "हमारा स्टडी कोच व्यस्त है — एक पल बाद कोशिश करें।",
  recommendedBooks: "सिफ़ारिश की गई पुस्तकें",
  done: "हो गया",
  back: "पीछे",
  diagnosticScoringFailed: "आपके उत्तर स्कोर करते समय समस्या। फिर कोशिश करें।",
  diagnosticStepPassedUnlock: "आप पास — अगला रोडमैप स्टेप अनलॉक।",
  diagnosticStepPassedOnly: "आपने यह स्टेप पास किया।",
  diagnosticStepFailedNeed70: (pct: number) =>
    `अगला स्टेप अनलॉक करने के लिए कम से कम 70% चाहिए। आपका स्कोर ${pct}%।`,
  diagnosticTopicsQueued: (n: number) => `${n} विषय रिव्यू कतार में, सबसे कमज़ोर पहले।`,
  diagnosticNothingToQueue: "कतार में कुछ नया नहीं — आप इन विषयों पर आगे हैं।",
  diagnosticStepQualified: "स्टेप क्वालिफ़ाई!",
  diagnosticNotQuiteYet: "अभी नहीं",
  diagnosticScoreSummary: (correct: number, asked: number) => `${correct} में से ${asked} सही`,
  diagnosticReviewStepRetry: "इस स्टेप की समीक्षा करें और क्वालिफ़ाई क्विज़ फिर लें।",
  diagnosticWhereToFocus: "कहाँ फोकस करें",
  diagnosticYourAnswers: "आपके उत्तर",
  diagnosticBackToRoadmap: "रोडमैप पर वापस",
  diagnosticTryAgainLater: "बाद में फिर कोशिश करें",
  percentComplete: (pct: number) => `${pct}% पूरा`,
  dayPlan: (days: number) => `~${days} दिन की योजना`,
  backToLearn: "सीखें पर वापस",
  yourRoadmap: "आपका रोडमैप",
  readForPleasureTitle: "बस पढ़ाई लॉग करें",
  readForPleasureBody: "मन से पढ़ाई योजनाओं में रोडमैप नहीं — सीखें टैब पर पढ़ाई लॉग करें।",
  roadmapLoadFailed: "आपका रोडमैप लोड नहीं हो सका।",
  generateRoadmapTitle: "अपना रोडमैप बनाएं",
  generateRoadmapBody: "आपकी योजना सेव है — अब दिन-दर-दिन मॉड्यूल और पुस्तक सुझाव बनाएं।",
  roadmapBuildingTitle: "आपका रोडमैप बन रहा है…",
  roadmapBuildingBody:
    "आपकी योजना सेव हो गई है। हम आपके दिन-दर-दिन मॉड्यूल और पुस्तक सुझाव तैयार कर रहे हैं — इसमें एक मिनट तक लग सकता है। आप ब्राउज़ करते रह सकते हैं; तैयार होते ही यह यहाँ दिखेगा।",
  noRoadmapModules: "इस योजना के लिए अभी कोई रोडमैप मॉड्यूल नहीं।",
  journeyProgress: "यात्रा प्रगति",
  courseRoadmapLoadFailed: "आपका कोर्स रोडमैप लोड नहीं हो सका।",
  gapRight: (correct: number, asked: number) => `${correct}/${asked} सही`,
  diagnosticYouPicked: "आपने चुना",
  diagnosticSkipped: "छोड़ा —",
  diagnosticAnswerIs: "सही उत्तर",
  loadingAria: "लोड हो रहा है",
  trackRecordActivityDetail: "गतिविधि विवरण",
  trackRecordSessionBreakdown: "सत्र विवरण",
  trackRecordLoadFailed: "यह सत्र लोड नहीं हो सका।",
  trackRecordNoDetail: "इस प्रविष्टि के लिए अतिरिक्त विवरण नहीं।",
  trackRecordScoreLabel: (pct: number) => `स्कोर: ${pct}%`,
  trackRecordEffectiveRecall: (tier: number, selfRated?: number | null) =>
    selfRated != null
      ? `प्रभावी रिकॉल स्तर: ${tier} (आपने ${selfRated} रेट किया)`
      : `प्रभावी रिकॉल स्तर: ${tier}`,
  trackRecordYourPick: " · आपकी पसंद",
  trackRecordCorrect: " · सही",
  certAttestation: (scorePct: number, streak: number, consistency: number, reviews: number) =>
    [`${scorePct}%`, hiCertEvidence(streak, consistency, reviews)].filter(Boolean).join(" · "),
  certEvidence: (streak: number, consistency: number, reviews: number) =>
    hiCertEvidence(streak, consistency, reviews),
  loggingForPlan: "योजना के लिए लॉग",
  roadmapStep: "रोडमैप स्टेप",
  pickRoadmapStep: "रोडमैप स्टेप चुनें (अनुशंसित)",
  newPlan: "नई योजना",
  trackRecord: "अभ्यास इतिहास",
  viewAll: "सभी देखें",
  showLess: "कम दिखाएँ",
  trackRecordEmpty: "पढ़ाई करते हुए आपके लॉग, क्विज़ और रिव्यू यहाँ दिखेंगे।",
  activePlan: "सक्रिय योजना",
  choosePlan: "आज आप कौन-सी योजना पढ़ रहे हैं?",
  questions: "प्रश्न",
  startDiagnostic: "डायग्नोस्टिक शुरू करें",
  startPractice: "अभ्यास टेस्ट शुरू करें",
  startAssessment: "मूल्यांकन शुरू करें",
  postReviewQuestions: "फॉलो-अप प्रश्न",
  buildingQuiz: "क्विज़ बन रही है…",
  followUpComplete: "फॉलो-अप पूरा",
  continue: "आगे बढ़ें",
  yourScore: "आपका स्कोर",
  reconciliationNote: "अनुसूची नोट",
  skillAssessment: "कौशल मूल्यांकन",
  skillHint: "एक कौशल में महारत साबित करें। 80% या अधिक पर प्रमाणपत्र मिलता है।",
  subject: "विषय",
  skillTopic: "मूल्यांकन का विषय",
  diagnosticTitle: "डायग्नोस्टिक क्विज़",
  diagnosticHint: "इस योजना के कमज़ोर विषय पहचानें।",
  diagnosticNoQuestions: "इस डायग्नोस्टिक में कोई प्रश्न नहीं हैं",
  diagnosticBuildFailed: "Ziggy इस योजना से क्विज़ नहीं बना सका। थोड़ी देर बाद फिर कोशिश करें।",
  scoring: "स्कोरिंग…",
  finishLabel: "समाप्त",
  skip: "स्किप",
  practiceTitle: "अभ्यास टेस्ट",
  postReviewHint: "अपनी याद की रेटिंग को मज़बूत करने के लिए त्वरित जाँच।",
  startFollowUp: "फॉलो-अप शुरू करें",
  runnerPrevious: "पिछला",
  runnerNext: "अगला",
  runnerSubmitting: "भेजा जा रहा है…",
  runnerSubmitFailed: "आपके उत्तर सबमिट नहीं हो सके।",
  runnerQuestionOf: (index: number, total: number) => `प्रश्न ${index} / ${total}`,
  runnerSubmitAssessment: "मूल्यांकन सबमिट करें",
  runnerSubmitFollowUp: "फॉलो-अप सबमिट करें",
  runnerFinishTest: "टेस्ट पूरा करें",
  runnerNoQuestionsAssessment: "इस मूल्यांकन में कोई प्रश्न नहीं है",
  runnerNoQuestionsTest: "इस टेस्ट में कोई प्रश्न नहीं है",
  runnerScoreBusy: "Ziggy अभी आपका टेस्ट स्कोर नहीं कर पाया। एक मिनट में फिर कोशिश करें।",
  runnerTestSubmitFailed: "टेस्ट सबमिट करते समय समस्या हुई। फिर कोशिश करें।",
  runnerCorrectOfAsked: (correct: number, asked: number) => `${asked} में से ${correct} सही`,
  runnerScoreFailed: "आपके उत्तर स्कोर करते समय समस्या। फिर कोशिश करें।",
  skillMastered: "कौशल में महारत",
  skillKeepPractising: "अभ्यास जारी रखें",
  skillPassedScore: (pct: number, topic: string) => `${topic} में आपने ${pct}% स्कोर किया।`,
  skillFailedScore: (pct: number) => `आपने ${pct}% स्कोर किया। प्रमाणपत्र के लिए 80% चाहिए।`,
  certificateIssued: "प्रमाणपत्र जारी",
  verifyCodeLabel: "सत्यापन कोड:",
  setupPending: "सेटअप बाकी",
  pathwayActive: "सक्रिय",
  progress: "प्रगति",
  finishSetup: "सेटअप पूरा करें",
  pause: "रोकें",
  resume: "जारी रखें",
  archive: "संग्रह",
  restore: "वापस लाएँ",
  delete: "हटाएँ",
  confirmDelete: "हटाने की पुष्टि",
  planActionOn: (action: string, planTitle: string) => `${action} — ${planTitle}`,
  statusPaused: "रुका हुआ",
  statusCompleted: "पूरा",
  statusArchived: "संग्रहित",
  skillSubjectTopicRequired: "विषय और कौशल विषय दर्ज करें।",
  skillAssessmentStartFailed: "मूल्यांकन शुरू नहीं हो सका।",
  skillSubjectPlaceholder: "भौतिकी",
  skillTopicPlaceholder: "प्रकाशिकी",
  yourCertificates: "आपके प्रमाणपत्र",
  copyVerifyCode: "सत्यापन कोड कॉपी करें",
  verifyCodeCopied: "सत्यापन कोड कॉपी हुआ",
  verifyCodeCopyFailed: "सत्यापन कोड कॉपी नहीं हो सका।",
  viewCertificate: "प्रमाणपत्र देखें",
  viewCertificateAria: (subject: string, topic: string) =>
    `प्रमाणपत्र देखें — ${subject} · ${topic}`,
  certificateOfMastery: "कौशल महारत का प्रमाणपत्र",
  verifiedCredential: "सत्यापित प्रमाणपत्र",
  issuedOn: "जारी",
  printCertificate: "प्रिंट / सेव करें",
  thisCredentialIsPublic:
    "इस लिंक से कोई भी प्रमाणपत्र की सत्यता जाँच सकता है — लॉगिन की ज़रूरत नहीं।",
  certificateInvalid:
    "यह प्रमाणपत्र सत्यापित नहीं हो सका। कोड जाँचें या सीखने वाले से नया लिंक माँगें।",
  certificateVerifyFailed: "प्रमाणपत्र अभी जाँचा नहीं जा सका। थोड़ी देर बाद कोशिश करें।",
  certificatesLoadFailed: "आपके प्रमाणपत्र लोड नहीं हो सके। जो आपने कमाए हैं वे सुरक्षित हैं।",
  pleasurePromoTitle: "अपनी पढ़ाई लॉग करें",
  pleasurePromoHint: (pathwayTitle: string) =>
    `${pathwayTitle} के लिए आपने जो पढ़ा उसे ट्रैक करें। कोई क्विज़ नहीं — बस आपकी पढ़ाई की डायरी।`,
  readLogTitle: "आज आपने क्या पढ़ा?",
  readLogTopicLabel: "विषय",
  readLogDescription: (pathwayTitle: string) =>
    `${pathwayTitle} से जो पढ़ा, लॉग करें। Ziggy कल रिकॉल चेक शेड्यूल करेगा।`,
  readLogMinutesOptional: "मिनट (वैकल्पिक)",
  readLogNoteOptional: "नोट (वैकल्पिक)",
  readLogMinutesPlaceholder: "30",
  readLogTopicPlaceholder: "ऊष्मागतिकी — हीट इंजन",
  readLogNotePlaceholder: "अध्याय 12, पृष्ठ 40–58",
  readLogSubjectTopicRequired: "विषय और टॉपिक ज़रूरी हैं।",
  readLogMinutesInvalid: "मिनट सकारात्मक संख्या होनी चाहिए।",
  readLogSaveFailed: "पढ़ाई लॉग सेव नहीं हुआ। फिर कोशिश करें।",
  readLogSaved: "लॉग हो गया — कल रिकॉल शेड्यूल।",
  readLogSaving: "सेव हो रहा है…",
  saveReadingLog: "पढ़ाई लॉग सेव करें",
  progressReportTitle: "प्रगति रिपोर्ट",
  progressReportDescription: (pathwayTitle: string) =>
    `${pathwayTitle} पर पिछले 7 दिनों का AI सारांश।`,
  progressReportNewUpdates: (n: number) => `${n} नया अपडेट — नीचे रिपोर्ट बनाएँ या खोलें।`,
  loadingReports: "रिपोर्ट लोड हो रही हैं",
  focusNext: "अगला फोकस",
  progressReportEmpty: "पहली रिपोर्ट बनाएँ — देखें पढ़ाई, रिव्यू और टेस्ट कैसे जुड़ रहे हैं।",
  finishWithAnswered: (answered: number, total: number) =>
    `समाप्त (${answered}/${total} उत्तर दिए)`,
  quizProgressAria: (current: number, total: number) => `प्रश्न ${current} / ${total}`,
  questionRange: (min: number, max: number) => `${min}–${max} प्रश्न`,
  topicsDueOnPlan: (count: number, planTitle: string) => `${planTitle} पर ${count} विषय बाकी`,
  switchPlanAria: (planTitle: string) => `योजना बदलें — अभी ${planTitle} पढ़ रहे हैं`,
  reviewProgressAria: (done: number, total: number) => `कार्ड ${done} / ${total}`,
  writingReport: "रिपोर्ट लिखी जा रही है…",
  generateFreshReport: "नई रिपोर्ट बनाएँ",
  generateProgressReport: "प्रगति रिपोर्ट बनाएँ",
  reportGeneratedOn: (date: string, days: number) => `पिछले ${days} दिनों की रिपोर्ट · ${date}`,
  reportReadyAnnounce: "नई रिपोर्ट तैयार है।",
  reportError429: "आज की रिपोर्ट सीमा पूरी — कल आएँ।",
  reportError503: "Ziggy अभी रिपोर्ट नहीं लिख सका। थोड़ी देर बाद कोशिश करें।",
  reportErrorGeneric: "रिपोर्ट बनाते समय समस्या। फिर कोशिश करें।",
  diagnosticError429: "आज की क्विज़ सीमा पूरी — कल आएँ।",
  diagnosticError503: "Ziggy अभी क्विज़ नहीं बना सका। एक मिनट बाद कोशिश करें।",
  diagnosticErrorGeneric: "क्विज़ शुरू करते समय समस्या। फिर कोशिश करें।",
  practiceError429: "आज की अभ्यास टेस्ट सीमा पूरी — कल आएँ।",
  practiceError503: "Ziggy अभी टेस्ट नहीं बना सका। एक मिनट बाद कोशिश करें।",
  practiceErrorGeneric: "अभ्यास टेस्ट शुरू करते समय समस्या। फिर कोशिश करें।",
  loading: "लोड हो रहा है…",
  plansLoadFailedTitle: "योजनाएँ लोड नहीं हुईं",
  plansLoadFailedBody: "आपकी पढ़ाई योजनाएँ लोड नहीं हुईं। फिर कोशिश करें।",
  moduleDisabledTitle: "लर्निंग ऐप चालू नहीं",
  moduleDisabledBody:
    "आपके स्टडी सेंटर ने अभी लर्निंग ऐप चालू नहीं किया। सेंटर एडमिन से पूछें — यहाँ से दोबारा कोशिश करने से काम नहीं बनेगा।",
  retry: "फिर कोशिश करें",
  buildStudyPlanTitle: "अपनी पढ़ाई योजना बनाएँ",
  buildStudyPlanHint:
    "Ziggy को बताएँ आप किस लक्ष्य की तैयारी कर रहे हैं — हम आपके लक्ष्य, किताबों और गति के हिसाब से योजना बनाएँगे।",
  startMyPlan: "मेरी योजना शुरू करें",
  practiceTopicsLabel: "विषय",
  addSubtopicPlaceholder: "उप-विषय जोड़ें",
  add: "जोड़ें",
  practicePickTopicsHint: (pathwayTitle: string) =>
    `${pathwayTitle} के विषय चुनें। Ziggy प्रश्न लिखेगा और फोकस बताएगा।`,
  finishLater: "बाद में खत्म करें",
  recallPrompt: "किताब बंद करें। जो याद आए लिखें या बोलें — फिर रेट करें कैसा रहा।",
  fromPathway: (pathwayTitle: string) => `${pathwayTitle} से`,
  iveTriedRateRecall: "कोशिश की — रेट करें",
  buildingFollowUpQuiz: "फॉलो-अप क्विज़ बन रही है…",
  saving: "सेव हो रहा है…",
  postQuizStartFailed: "फॉलो-अप क्विज़ शुरू नहीं हुई। फिर कोशिश करें।",
  postQuizUnavailable: "Ziggy अभी यह ज़रूरी फॉलो-अप क्विज़ नहीं बना सका। फिर कोशिश करें।",
  reviewSaveFailed: "रिव्यू सेव नहीं हुआ। फिर कोशिश करें।",
  queueCleared: "कतार पूरी",
  queueClearedHint: (reviewed: number) =>
    `${reviewed} विषय रिव्यू। हर एक थोड़ी देर बाद वापस — यही याद रखता है।`,
  backToPlans: "मेरी योजनाओं पर वापस",
  gradeBlanked: "याद नहीं",
  gradeBlankedHint: "कुछ नहीं आया",
  gradeShaky: "हिचकिचाहट",
  gradeShakyHint: "धीरे-धीरे याद आया",
  gradeSolid: "ठीक",
  gradeSolidHint: "याद आ गया",
  gradeInstant: "तुरंत",
  gradeInstantHint: "पक्का याद था",
  roadmapScrollAria: (title: string) => `${title} — सड़क पर आगे बढ़ने के लिए स्क्रॉल करें`,
  roadmapRoleDescription: "3D रोडमैप",
  roadmapKeyboardHint: "चेकपॉइंट बदलने के लिए तीर कुंजियाँ दबाएँ",
  roadmapNoSteps: "अभी कोई रोडमैप चरण नहीं है।",
  roadmapListFallback:
    "सूची दृश्य दिखा रहे हैं — इस ब्राउज़र में 3D के लिए ग्राफ़िक्स सहायता नहीं है।",
  roadmapCurrentStep: "मौजूदा चरण",
  roadmapCheckpointAnnounce: (title: string) => `चेकपॉइंट: ${title}`,
  checkpointNumbered: (n: number) => `चेकपॉइंट ${n}`,
  phaseLabel: "चरण",
  readsLogged: (n: number) => `${n} बार पढ़ा`,
  noReadsYet: "अभी कोई पढ़ाई दर्ज नहीं",
  qualifyWithQuestions: (n: number) => `क्वालिफाई · ${n}+ प्रश्न`,
  coachGuideLabel: "जिग्गी · राह दिखाने वाला",
  coachScrollForward: "आगे बढ़ने के लिए स्क्रॉल करें — आपका सफ़र आगे खुल रहा है।",
  coachEnteringPhase: (title: string) =>
    `${title} शुरू। इस चरण को पूरा करने के लिए हर चेकपॉइंट पार करें।`,
  coachCheckpointCleared: "चेकपॉइंट पूरा! फ़िनिश की ओर बढ़ते रहें।",
  coachQualifyStep: "आप यहाँ हैं — पढ़ाई दर्ज करें, फिर इस चरण को क्वालिफाई करें।",
  coachStepOpen: "यह चरण खुला है। पढ़ें, अभ्यास करें, आगे बढ़ें।",
  coachStepLocked: "इसे खोलने के लिए पिछला चेकपॉइंट पूरा करें।",
  stepCompleteTitle: "चरण पूरा!",
  stepCompleteBody: "अगला मॉड्यूल खुल गया — सिलसिला बनाए रखें",
  recommendedReads: "सुझाई गई किताबें",
  recommendedReadsHint: "आगे बढ़ते हुए इन्हें पढ़ें — शेल्फ़ देखने के लिए स्वाइप करें।",
  nextUp: (topic: string) => `आगे · ${topic}`,
  yourProgress: "आपकी प्रगति",
  tapToOpenPlan: "अपनी योजना खोलने के लिए टैप करें",
  continueLabel: "जारी रखें",
  planProgressAria: (title: string, pct: number) => `${title} — ${pct}% पूरा`,
  readsLoggedCount: (n: number) => `${n} बार पढ़ा दर्ज`,
  noReadsLoggedYet: "अभी कोई पढ़ाई दर्ज नहीं",
  qualifyWithQuestionsLong: (n: number) => `क्वालिफाई (${n}+ प्रश्न)`,
  estimatedMinutes: (n: number) => `~${n} मिनट`,
  subjectProgressAria: (subject: string, done: number, total: number) =>
    `${subject} — ${total} में से ${done} चरण पूरे`,
  yourCentreFallback: "आपका सेंटर",
  learnerFallbackName: "दोस्त",
};

type LearnStringTable = {
  [K in keyof typeof EN]: (typeof EN)[K] extends (...args: infer A) => infer R
    ? (...args: A) => R
    : string;
};

const TABLES: Record<LearnLocale, LearnStringTable> = {
  en: EN,
  hi: HI as LearnStringTable,
};

export function learnStrings(locale: LearnLocale) {
  return TABLES[locale] ?? EN;
}

export const LEARN_LOCALE_STORAGE_KEY = "eduviaos.learnLocale";

export function readLearnLocaleOverride(): LearnLocale | null {
  try {
    const raw = localStorage.getItem(LEARN_LOCALE_STORAGE_KEY);
    return raw === "hi" || raw === "en" ? raw : null;
  } catch {
    return null;
  }
}

export function writeLearnLocaleOverride(locale: LearnLocale) {
  try {
    localStorage.setItem(LEARN_LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore
  }
}
